import trapFocus from '../../utils/trapFocus';
import initAtcPopup from '../../components/atc-popup';
import { debounce } from 'throttle-debounce';
import { SETTINGS, CLASSES } from '../../utils/constants';

const FILTER_INPUT_SELECTOR = '[data-facet-filter] input';

const initCollectionSearchFilters = () => {
  const filtersContainer = document.querySelector('[data-filters-container]');
  if (!filtersContainer) return;

  const productGridEl = document.querySelector('[data-product-grid]');
  const paginationEl = document.querySelector('[data-pagination]');
  const filterDrawer = filtersContainer.querySelector('[data-filter-drawer]');
  const openDrawerBtn = filtersContainer.querySelector('[data-open-filter-drawer]');
  const closeDrawerBtn = filtersContainer.querySelector('[data-close-filter-drawer-button]');
  const filterFormEl = filtersContainer.querySelector('[data-filter-form]');
  const filterDrawerSubmitBtn = filtersContainer.querySelector('[data-filters-submit]');  
  const filterDrawerClearAllBtn = filtersContainer.querySelector('[data-filters-clear-all]');
  const resultsCount = filtersContainer.querySelector('[data-filters-results-count]');
  const activeFiltersContainerEl = filtersContainer.querySelector('[data-active-filters]');
  const activeFilterCount = filtersContainer.querySelector('[data-active-filter-count]');

  const sectionId = filterDrawer.dataset.sectionId;

  // declare renderActiveFilters function here to avoid hoisting issues
  // and to allow it to be used in the renderPage function
  let renderActiveFilters = () => {};

  const fetchResults = async (url) => {
    try {
      const response = await fetch(url);
      const html = await response.text();
      return new DOMParser().parseFromString(html, 'text/html');
    } catch (err) {
      console.error('Error fetching filter results:', err);
    }
  };

  const renderProductGrid = (resultsDoc) => {
    if (!resultsDoc) return;

    const mappings = [
      { oldEl: productGridEl, newEl: '[data-product-grid]' },
      { oldEl: paginationEl, newEl: '[data-pagination]' },
      { oldEl: resultsCount, newEl: '[data-filters-results-count]' },
      { oldEl: activeFilterCount, newEl: '[data-active-filter-count]' },
    ];
    
    mappings.forEach(({ oldEl, newEl }) => {
      const newContent = resultsDoc.querySelector(newEl);
      if (oldEl && newContent) oldEl.innerHTML = newContent.innerHTML;
    });
    initAtcPopup();
    if (typeof window.ReloadSmartWishlist === 'function') {
      ReloadSmartWishlist();
    }
  };

  const getFilterInputKey = (inputEl) => (
    inputEl.type === 'checkbox' ? `${inputEl.name}|${inputEl.value}` : inputEl.id
  );

  const replaceFilterValueLists = (newFiltersFormEl) => {
    const focusedEl = document.activeElement;
    const focusedKey = filterFormEl.contains(focusedEl) && focusedEl.matches('input')
      ? getFilterInputKey(focusedEl)
      : null;

    filterFormEl.querySelectorAll('[data-filter-values]').forEach(listEl => {
      const newListEl = newFiltersFormEl.querySelector(
        `[data-filter-values="${CSS.escape(listEl.dataset.filterValues)}"]`
      );
      if (newListEl) listEl.innerHTML = newListEl.innerHTML;
    });

    if (!focusedKey) return;
    const refocusEl = [...filterFormEl.querySelectorAll(FILTER_INPUT_SELECTOR)]
      .find(inputEl => getFilterInputKey(inputEl) === focusedKey);
    refocusEl?.focus();
  };

  const renderFilters = (resultsDoc) => {
    if (!resultsDoc) return;

    const newFiltersSubmitBtn = resultsDoc.querySelector('[data-filters-submit]');
    if (newFiltersSubmitBtn) {
      filterDrawerSubmitBtn.innerHTML = newFiltersSubmitBtn.innerHTML;
    }
    const newFiltersFormEl = resultsDoc.querySelector('[data-filter-form]');
    if (!newFiltersFormEl) return;

    replaceFilterValueLists(newFiltersFormEl);

    const newInputEls = newFiltersFormEl.querySelectorAll(FILTER_INPUT_SELECTOR);
    const newInputsByKey = new Map([...newInputEls].map(inputEl => [getFilterInputKey(inputEl), inputEl]));

    filterFormEl.querySelectorAll(FILTER_INPUT_SELECTOR).forEach(existingInputEl => {
      const newInputEl = newInputsByKey.get(getFilterInputKey(existingInputEl));
      if (!newInputEl) {
        existingInputEl.disabled = true;
        return;
      }
      existingInputEl.disabled = newInputEl.disabled;
      existingInputEl.checked = newInputEl.checked;
      // Checkbox values are the match key and never change; only price inputs
      // carry a server-side value that needs syncing.
      if (existingInputEl.type !== 'checkbox') existingInputEl.value = newInputEl.value;
    });
  };

  const runRenderer = (renderer, resultsDoc) => {
    try {
      renderer(resultsDoc);
    } catch (err) {
      console.error(`Error in ${renderer.name}:`, err);
    }
  };

  const renderPage = async (url) => {
    const fetchUrl = sectionId ? `${url}&section_id=${sectionId}` : url;
    const resultsDoc = await fetchResults(fetchUrl);
    if (!resultsDoc) return;
    [renderFilters, renderActiveFilters, renderProductGrid].forEach(renderer => runRenderer(renderer, resultsDoc));
  };

  const handleFilterLinkClick = async (e) => {
    e.preventDefault();
    const closestLink = e.target.closest('a');
    if (!closestLink || !closestLink.href) return;
    
    const urlHref = closestLink.href;
    window.history.pushState({}, '', urlHref);
    await renderPage(urlHref);
  };

  const setUpActiveFilterEventListeners = () => {
    activeFiltersContainerEl.addEventListener('click', (e) => {
      const activeFilterEl = e.target.closest('[data-active-filter]');
      if (activeFilterEl) {
        handleFilterLinkClick(e);
      }
    });
  };

  renderActiveFilters = (resultsDoc) => {
    if (!resultsDoc) return;

    const newActiveFiltersEl = resultsDoc.querySelector('[data-active-filters]');
    if (!newActiveFiltersEl) return;

    activeFiltersContainerEl.innerHTML = newActiveFiltersEl.innerHTML;
  };

  const convertFormDataToUrl = () => {
    const formData = new FormData(filterFormEl);
    const params = new URLSearchParams(formData).toString();
    const url = new URL(window.location.href);
    url.search = params;
    return url;
  };

  const handleFormInput = async () => {
    const url = convertFormDataToUrl();
    if (!url) return;

    window.history.pushState({}, '', url);
    renderPage(url.href);
  };

  const debouncedFormInput = debounce(SETTINGS.FILTER_UPDATE_DELAY, handleFormInput);

  const closeFilterDrawer = () => {
    filterDrawer.classList.remove(CLASSES.TRANSLATE_X_0);
    setTimeout(() => {
      filterDrawer.close();
    }, SETTINGS.FILTER_DRAW_ANIMATION_TIME);
  };

  const handleCloseFilterDrawerClick = () => {
    closeFilterDrawer();
  };

  const handleFilterDrawerClick = e => {
    if (!filterDrawer) return;
    const isInDialog = filterDrawer.getBoundingClientRect().right > e.clientX;
    if (!isInDialog) {
      closeFilterDrawer();
    }
  };

  const openFilterDrawer = () => {
    filterDrawer.showModal();
    //uses requestAnimationFrame to ensure the class is added after the modal is displayed (on safari)
    //without this, the transition does not work correctly on Safari
    requestAnimationFrame(() => {
      filterDrawer.classList.add(CLASSES.TRANSLATE_X_0);
    });
    trapFocus(filterDrawer);
    filterDrawer.addEventListener('click', handleFilterDrawerClick);
    filterFormEl.addEventListener('input', debouncedFormInput);
    filterDrawerSubmitBtn.addEventListener('click', handleCloseFilterDrawerClick);
    filterDrawerClearAllBtn.addEventListener('click', handleFilterLinkClick);
    closeDrawerBtn.addEventListener('click', handleCloseFilterDrawerClick);
  };

  openDrawerBtn.addEventListener('click', () => openFilterDrawer());

  setUpActiveFilterEventListeners();

  filterDrawer.addEventListener('close', () => {
    filterDrawer.removeEventListener('click', handleFilterDrawerClick);
    filterFormEl.removeEventListener('input', debouncedFormInput);
    filterDrawerSubmitBtn.removeEventListener('click', handleCloseFilterDrawerClick);
    closeDrawerBtn.removeEventListener('click', handleCloseFilterDrawerClick);
    filterDrawerClearAllBtn.removeEventListener('click', handleFilterLinkClick);
    openDrawerBtn.focus();
  });
};

export default initCollectionSearchFilters;